import { NextPage } from "next"
import Error from "next/error"
import Head from "next/head"
import React from "react"
import styled, { css } from "styled-components"

import Typography from "~/components/atoms/Typography"
import { Container } from "~/components/layouts/Container"
import Breadcrumb from "~/components/organisms/Breadcrumb"
import DetailMenu from "~/components/organisms/DetailMenu"
import Footer from "~/components/organisms/Footer"
import Header from "~/components/organisms/Header"
import { IsPC, IsMobile, SpacingSize, Z } from "~/consts/style-mixins"
import RecruitmentList from "~/observers/RecruitmentList"
import { internalLink } from "~/services/helper"
import { InfraNextPageContext } from "~/types/InfraNextPageContext"
import { ReturnGetInitialProps } from "~/types/ReturnGetInitialProps"

const getInitialProps = async ({ initialStoreData: { companyStore }, query, res }: InfraNextPageContext) => {
  const id = Number(query["company-id"])
  const company = await companyStore.fetchById(id)
  if (!company || !company.id) {
    res.statusCode = 404
    return {}
  }
  await companyStore.fetchAllRecruitments(id)

  const newGradJobs = companyStore.newGradJobs
  const shortInternships = companyStore.shortInternships
  const longInternships = companyStore.longInternships
  const events = companyStore.events
  return { company, newGradJobs, shortInternships, longInternships, events }
}

type GetInitialProps = ReturnGetInitialProps<typeof getInitialProps>

const Page: NextPage<GetInitialProps> = ({ company, newGradJobs, shortInternships, longInternships, events }) => {
  if (!company || !company.id) {
    return <Error statusCode={404} />
  }

  const crumbs = [
    {
      name: "インターンシップ・新卒採用情報掲載企業一覧",
      link: internalLink(`/companies`),
    },
    {
      name: `${company.name}のインターン・新卒採用情報`,
      link: internalLink(`/companies/${company.id}`),
    },
  ]
  return (
    <>
      <Head>
        <title>{company.name}の募集一覧 | Infra（インフラ）</title>
        <meta property="og:title" content={`${company.name}の募集一覧 | Infra（インフラ）`} />
        <meta property="og:description" content={`Infra（インフラ）では${company.name}の募集を掲載しています。`} />
        <meta property="og:image" content={company.logoImgUrl} />
      </Head>
      <Header logoType="Plain" showTab />
      <Container size="Large">
        <StyledInnerContainer>
          <StyledDetailMenuWrapper>
            <DetailMenu company={company} selectedTabIndex={2} />
          </StyledDetailMenuWrapper>
          <StyledMainContentWrapper>
            <StyledMainContentInner>
              {(newGradJobs ?? []).length !== 0 && (
                <StyledEachRecruitmentListWrapper>
                  <StyledHeadingTypoGraphy type="SubHero">新卒求人</StyledHeadingTypoGraphy>
                  <StyledEachRecruitmentList>
                    <StyledRecruitmentList
                      type="newGradJob"
                      recruitments={newGradJobs}
                      inlineOnSp={true}
                      cardWidthOnPc={48}
                    />
                  </StyledEachRecruitmentList>
                </StyledEachRecruitmentListWrapper>
              )}
              {(shortInternships ?? []).length !== 0 && (
                <StyledEachRecruitmentListWrapper>
                  <StyledHeadingTypoGraphy type="SubHero">短期インターン</StyledHeadingTypoGraphy>
                  <StyledEachRecruitmentList>
                    <StyledRecruitmentList
                      type="shortInternship"
                      recruitments={shortInternships}
                      inlineOnSp={true}
                      cardWidthOnPc={48}
                    />
                  </StyledEachRecruitmentList>
                </StyledEachRecruitmentListWrapper>
              )}
              {(longInternships ?? []).length !== 0 && (
                <StyledEachRecruitmentListWrapper>
                  <StyledHeadingTypoGraphy type="SubHero">長期インターン</StyledHeadingTypoGraphy>
                  <StyledEachRecruitmentList>
                    <StyledRecruitmentList
                      type="longInternship"
                      recruitments={longInternships}
                      inlineOnSp={true}
                      cardWidthOnPc={48}
                    />
                  </StyledEachRecruitmentList>
                </StyledEachRecruitmentListWrapper>
              )}
              {(events ?? []).length !== 0 && (
                <StyledEachRecruitmentListWrapper>
                  <StyledHeadingTypoGraphy type="SubHero">イベント・説明会</StyledHeadingTypoGraphy>
                  <StyledEachRecruitmentList>
                    <StyledRecruitmentList type="event" recruitments={events} inlineOnSp={true} cardWidthOnPc={48} />
                  </StyledEachRecruitmentList>
                </StyledEachRecruitmentListWrapper>
              )}
            </StyledMainContentInner>
          </StyledMainContentWrapper>
        </StyledInnerContainer>
      </Container>
      <Breadcrumb crumbs={crumbs} />
      <Footer />
    </>
  )
}

Page.getInitialProps = getInitialProps

export default Page

const StyledInnerContainer = styled.div`
  ${IsPC(css`
    padding-top: 48px;
    display: flex;
    flex-wrap: wrap;
    min-height: calc(100vh - 215px);
  `)}
  ${IsMobile(css`
    min-height: calc(100vh - 330px);
  `)}
`

const StyledDetailMenuWrapper = styled.div`
  position: sticky;
  z-index: ${Z.Tab};
  ${IsPC(css`
    top: 16px;
    height: 100%;
  `)}
  ${IsMobile(css`
    top: 0;
  `)}
`

const StyledMainContentWrapper = styled.div`
  width: 100%;
  ${IsPC(css`
    width: calc(100% - 280px);
  `)}
  ${IsMobile(css`
    padding: 24px 0 8px;
  `)}
`

const StyledMainContentInner = styled.div`
  ${IsPC(css`
    padding-left: 48px;
  `)}
`

const StyledEachRecruitmentListWrapper = styled.div`
  margin-bottom: ${SpacingSize.XL}px;
  ${IsMobile(css`
    width: 100%;
    margin-bottom: 32px;
  `)}
`

const StyledEachRecruitmentList = styled.div`
  display: flex;
  align-items: center;
  ${IsMobile(css`
    overflow-x: scroll;
    margin: 0 -16px;
    padding: 0 16px;
    &::-webkit-scrollbar {
      display: none;
    }
  `)}
  ${IsPC(css`
    flex-wrap: wrap;
    margin: 0 -8px;
  `)}
`

const StyledHeadingTypoGraphy = styled(Typography)`
  margin-bottom: 12px;
`

const StyledRecruitmentList = styled(RecruitmentList)`
  ${IsMobile(css`
    width: 296px;
    min-width: 296px;
    padding-right: 16px;
    &:last-child {
      padding-right: 32px;
    }
  `)}
  ${IsPC(css`
    width: calc(50% - 16px);
    padding: 0 8px 24px;
  `)}
`
